import {
  Card,
  CardActionArea,
  CardActions,
  CardContent,
  CardHeader,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  TextField,
  Tooltip,
} from "@material-ui/core";
import Button from "@material-ui/core/Button";
import { createStyles, makeStyles, Theme } from "@material-ui/core/styles";
import DownloadIcon from "@material-ui/icons/CloudDownload";
import DeleteIcon from "@material-ui/icons/Delete";
import InfoIcon from "@material-ui/icons/Info";
import { TextEditStyle } from "@styled-icons/fluentui-system-filled";
import { loadCSS } from "fg-loadcss";
import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";

import { CustomerGroupListItem } from "../../../lib/models/CustomerGroupListItem";
import { Dispatch, StoreState } from "../../../store";
import ConfirmationDialog from "../../generic/ConfirmationDialog";
import ChangeFilterSettingForCustomerGroupDialog from "../ChangeFilterSettingForCustomerGroupDialog";
import CustomerGroupExportList from "./CustomerGroupExport/CustomerGroupExportList";
import FilterSettingsPopover from "./FilterSettingsPopover";

interface Props {
  customerGroup: CustomerGroupListItem;
  selected: boolean;
}

export default function CustomerGroup({ customerGroup, selected }: Props) {
  const classes = useStyles();
  const dispatch = useDispatch<Dispatch>();
  const [showRenameDialog, setShowRenameDialog] = useState(false);
  const [newNameCustomerGroup, setNewNameCustomerGroup] = useState(
    customerGroup.groupName
  );
  const [
    showDialogChangeFilterSettings,
    setShowDialogChangeFilterSettings,
  ] = useState(false);

  useEffect(() => {
    const node = loadCSS(
      "https://use.fontawesome.com/releases/v5.12.0/css/all.css",
      (document.querySelector("#font-awesome-css") as HTMLElement | null) ??
        undefined
    );

    return () => {
      node.parentNode!.removeChild(node);
    };
  }, []);

  const onSelectCustomerGroup = () => {
    dispatch.activeCustomerGroup.setActiveCustomerGroup(customerGroup);
    dispatch.customerGroupFilter.changeCustomerGroupFilter(
      customerGroup.filterSettings
    );
  };

  const activeCustomerGroup = useSelector(
    (state: StoreState) => state.activeCustomerGroup
  );

  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
  const onShowExports = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };
  const onCloseExports = () => {
    setAnchorEl(null);
  };

  const [showConfirmDeleteDialog, setShowConfirmDeleteDialog] = useState(false);
  const onDeleteCustomerGroup = () => {
    setShowConfirmDeleteDialog(true);
  };

  const handleCloseDelete = () => {
    setShowConfirmDeleteDialog(false);
  };

  const handleConfirmDelete = () => {
    if (activeCustomerGroup?.uuid === customerGroup.uuid) {
      dispatch.activeCustomerGroup.setActiveCustomerGroup(null);
    }
    dispatch.customerGroupList.deleteCustomerGroup(customerGroup.uuid);
    setShowConfirmDeleteDialog(false);
  };

  const handleRenameCustomerGroup = () => {
    setShowRenameDialog(false);
    dispatch.customerGroupList.editCustomerGroup({
      groupName: newNameCustomerGroup,
      customerGroupUuid: customerGroup.uuid,
    });
  };

  const handleAbortRenameCustomerGroup = () => {
    setShowRenameDialog(false);
    setNewNameCustomerGroup(customerGroup.groupName);
  };

  return (
    <>
      <Grid item key={customerGroup.uuid}>
        <Card
          className={`${classes.card} ${selected ? classes.cardActive : ""}`}
        >
          <CardActionArea onClick={onSelectCustomerGroup}>
            <CardHeader
              title={customerGroup.groupName}
              subheader={customerGroup.lastModificationAt.toLocaleDateString()}
              titleTypographyProps={{ variant: "h6" }}
              subheaderTypographyProps={{ variant: "body2" }}
              action={
                <Tooltip title={<FilterSettingsPopover item={customerGroup} />}>
                  <IconButton disableRipple component="div">
                    <InfoIcon />
                  </IconButton>
                </Tooltip>
              }
            />
            <CardContent className={classes.content} />
          </CardActionArea>
          <CardActions disableSpacing>
            <Tooltip title="Löschen">
              <IconButton onClick={onDeleteCustomerGroup}>
                <DeleteIcon />
              </IconButton>
            </Tooltip>
            <Tooltip title="Namen ändern">
              <IconButton
                onClick={() => {
                  setShowRenameDialog(true);
                }}
              >
                <TextEditStyle size={24} />
              </IconButton>
            </Tooltip>
            {/* <Tooltip title="Filter übernehmen">
              <IconButton
                onClick={() => {
                  setShowDialogChangeFilterSettings(true);
                }}
              >
                <Icon className="fas fa-filter fa-xs" />
              </IconButton>
            </Tooltip> */}
            <Tooltip
              title="Gespeicherte Kundennummern-Exporte"
              className={classes.buttonRight}
            >
              <IconButton onClick={onShowExports}>
                <DownloadIcon />
              </IconButton>
            </Tooltip>
          </CardActions>
        </Card>
      </Grid>

      <CustomerGroupExportList
        open={!!anchorEl}
        customerGroup={customerGroup}
        anchorEl={anchorEl}
        handleCloseCustomerGroupExports={onCloseExports}
      />

      <ConfirmationDialog
        open={showConfirmDeleteDialog}
        onClose={handleCloseDelete}
        onConfirm={handleConfirmDelete}
        onAbort={handleCloseDelete}
        text={`Soll die Kundengruppe "${customerGroup.groupName}" gelöscht werden?`}
      />
      <Dialog
        open={showRenameDialog}
        onClose={() => setShowRenameDialog(false)}
        onKeyUp={(e) => {
          if (e.key === "Enter") {
            handleRenameCustomerGroup();
          }
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle> Kundengruppe umbenennen</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            id="name"
            label="Name der Kundengruppe"
            fullWidth
            margin="dense"
            value={newNameCustomerGroup}
            onChange={(e) => setNewNameCustomerGroup(e.target.value)}
            helperText={
              newNameCustomerGroup.length === 0 ? "Bitte Namen eingeben" : ""
            }
            error={newNameCustomerGroup.length === 0}
          />
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => handleAbortRenameCustomerGroup()}
            color="primary"
          >
            Abbrechen
          </Button>
          <Button
            onClick={() => handleRenameCustomerGroup()}
            color="primary"
            disabled={newNameCustomerGroup.length === 0}
          >
            Umbenennen
          </Button>
        </DialogActions>
      </Dialog>
      <ChangeFilterSettingForCustomerGroupDialog
        open={showDialogChangeFilterSettings}
        customerGroup={customerGroup}
        onClose={() => setShowDialogChangeFilterSettings(false)}
        onConfirm={() => {
          setShowDialogChangeFilterSettings(false);
        }}
        onAbort={() => {
          setShowDialogChangeFilterSettings(false);
        }}
      />
    </>
  );
}

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    card: {
      minWidth: 220,
      minHeight: "100%",
      backgroundColor: theme.palette.background.default,
      transition: "background-color 150ms linear",
      borderBottomStyle: "none",
    },
    cardActive: {
      backgroundColor: "#EDFAFF",
      borderBottomColor: "#00A8E1",
      borderBottomStyle: "solid",
      borderBottomWidth: "medium",
      transition: "background-color 150ms linear",
    },
    content: {
      height: 40,
    },
    buttonRight: {
      marginLeft: "auto",
    },
  })
);



// WEBPACK FOOTER //
// ./src/components/CustomerGroups/CustomerGroup/CustomerGroup.tsx